import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  where,
  writeBatch,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { generateSecureCode, randomId } from '@/utils/random';
import { getReadingProgress } from '@/services/readingProgress';
import type {
  InviteCode,
  UserProfile,
  AuthProvider,
  PrayerType,
  UserRole,
  UserStatus,
  AttendanceEntry,
  AdminAuditEntry,
  MfaEnforcementType,
} from '@/types';
import type {
  DateCount,
  AdminUserStats,
  EngagementStats,
  InviteCodeStats,
  AdminMessage,
  ActivityEvent,
  CreateInviteCodeOptions,
  AdminUserProfile,
  LiveStats,
  LeaderboardEntry,
  StreakDistribution,
  DayOfWeekStats,
  RetentionMetrics,
  LeaderboardType,
  EmojiStats,
  EmojiCount,
  ReactedMessage,
  ReactionDetail,
  TopReactor,
} from '@/types/admin';

export type { AdminMessage };

// ============================================
// LAZY-LOADED FIREBASE FUNCTIONS
// ============================================

// Lazy-loaded Firebase Functions module to reduce initial bundle size
let functionsModule: typeof import('firebase/functions') | null = null;
let functionsInstance: import('firebase/functions').Functions | null = null;

/**
 * Lazily load Firebase Functions module and instance.
 * Only loads when admin/moderation features are actually used.
 */
async function getFirebaseFunctions(): Promise<{
  httpsCallable: typeof import('firebase/functions').httpsCallable;
  functions: import('firebase/functions').Functions;
}> {
  if (!functionsModule || !functionsInstance) {
    const [mod, { app }] = await Promise.all([
      import('firebase/functions'),
      import('@/config/firebase'),
    ]);
    functionsModule = mod;
    functionsInstance = mod.getFunctions(app);
  }
  return {
    httpsCallable: functionsModule.httpsCallable,
    functions: functionsInstance,
  };
}

// ============================================
// USER CACHE
// ============================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// In-memory cache for user data with TTL
const userCache: {
  allUsers: CacheEntry<AdminUserProfile[]> | null;
} = {
  allUsers: null,
};

// Promise cache to prevent concurrent fetch race conditions
let fetchUsersPromise: Promise<AdminUserProfile[]> | null = null;

const USER_CACHE_TTL_MS = 60 * 1000; // 1 minute cache for frequently accessed data

function isCacheValid<T>(cache: CacheEntry<T> | null): cache is CacheEntry<T> {
  if (!cache) return false;
  return Date.now() - cache.timestamp < USER_CACHE_TTL_MS;
}

/**
 * Clear the user cache - call after user updates
 */
export function invalidateUserCache(): void {
  userCache.allUsers = null;
  fetchUsersPromise = null;
}

// ============================================
// USER ANALYTICS
// ============================================

export async function getAllUsers(): Promise<AdminUserProfile[]> {
  // Return cached data if valid
  if (isCacheValid(userCache.allUsers)) {
    return userCache.allUsers.data;
  }

  // If there's already a fetch in progress, wait for it to prevent race conditions
  if (fetchUsersPromise) {
    return fetchUsersPromise;
  }

  // Create the fetch promise
  fetchUsersPromise = (async () => {
    try {
      const users: AdminUserProfile[] = [];

      // Get all user profiles by querying the users collection
      const usersRef = collection(db, 'artifacts', appId, 'users');
      const usersSnapshot = await getDocs(query(usersRef, limit(500)));

      for (const userDoc of usersSnapshot.docs) {
        try {
          // Get profile and stats documents for each user
          const profileSnap = await getDocs(
            collection(db, 'artifacts', appId, 'users', userDoc.id, 'profile')
          );

          const infoDoc = profileSnap.docs.find((d) => d.id === 'info');
          const statsDoc = profileSnap.docs.find((d) => d.id === 'stats');

          if (infoDoc?.exists()) {
            const profile = infoDoc.data() as UserProfile;
            const stats = statsDoc?.exists() ? statsDoc.data() : undefined;
            users.push({ ...profile, stats: stats as AdminUserProfile['stats'] });
          }
        } catch (error) {
          console.error(`Error fetching user ${userDoc.id}:`, error);
        }
      }

      // Cache the result
      userCache.allUsers = {
        data: users,
        timestamp: Date.now(),
      };

      return users;
    } finally {
      // Clear the promise so future calls can start a new fetch
      fetchUsersPromise = null;
    }
  })();

  return fetchUsersPromise;
}

export async function getUserStats(): Promise<AdminUserStats> {
  const users = await getAllUsers();

  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const byProvider: Record<AuthProvider, number> = {
    google: 0,
    microsoft: 0,
    apple: 0,
    password: 0,
    emailLink: 0,
  };

  let newUsers = 0;
  let activeUsers = 0;

  for (const user of users) {
    // Count by provider
    byProvider[user.provider] = (byProvider[user.provider] || 0) + 1;

    // Count new users (last 30 days)
    if (user.createdAt?.toDate() > thirtyDaysAgo) {
      newUsers++;
    }

    // Count active users (last 7 days)
    const lastActive = user.lastActiveAt?.toDate() || user.lastLoginAt?.toDate();
    if (lastActive && lastActive > sevenDaysAgo) {
      activeUsers++;
    }
  }

  return {
    total: users.length,
    new: newUsers,
    active: activeUsers,
    byProvider,
  };
}

// ============================================
// USER MANAGEMENT
// ============================================

export async function updateUserRole(userId: string, role: UserRole): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');

    // Get current role before update to determine promotion/demotion
    const currentProfileSnap = await getDoc(profileRef);
    const currentRole = currentProfileSnap.exists()
      ? (currentProfileSnap.data().role as UserRole) || 'member'
      : 'member';

    await updateDoc(profileRef, {
      role,
      isAdmin: role === 'admin',
    });

    // Handle Leaders Chat metadata on role changes
    const wasLeaderOrAdmin = currentRole === 'leader' || currentRole === 'admin';
    const isLeaderOrAdmin = role === 'leader' || role === 'admin';

    if (!wasLeaderOrAdmin && isLeaderOrAdmin) {
      // Promotion to leader/admin: initialize Leaders Chat metadata
      const { initLeadersChatMetadata } = await import('@/services/leadersChat');
      await initLeadersChatMetadata(userId);
    } else if (wasLeaderOrAdmin && !isLeaderOrAdmin) {
      // Demotion to member: remove Leaders Chat metadata
      const { removeLeadersChatMetadata } = await import('@/services/leadersChat');
      await removeLeadersChatMetadata(userId);
    }
  } catch (error) {
    console.error(`[Admin] Failed to update role for user ${userId}:`, error);
    throw new Error(
      `Failed to update user role: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

export async function suspendUser(userId: string, reason: string, adminId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    await updateDoc(profileRef, {
      status: 'suspended' as UserStatus,
      suspendedAt: serverTimestamp(),
      suspendedBy: adminId,
      suspendedReason: reason,
    });
  } catch (error) {
    console.error(`[Admin] Failed to suspend user ${userId}:`, error);
    throw new Error(
      `Failed to suspend user: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

export async function banUser(userId: string, reason: string, adminId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    await updateDoc(profileRef, {
      status: 'banned' as UserStatus,
      suspendedAt: serverTimestamp(),
      suspendedBy: adminId,
      suspendedReason: reason,
    });
  } catch (error) {
    console.error(`[Admin] Failed to ban user ${userId}:`, error);
    throw new Error(
      `Failed to ban user: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

export async function reactivateUser(userId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    await updateDoc(profileRef, {
      status: 'active' as UserStatus,
      suspendedAt: null,
      suspendedBy: null,
      suspendedReason: null,
    });
  } catch (error) {
    console.error(`[Admin] Failed to reactivate user ${userId}:`, error);
    throw new Error(
      `Failed to reactivate user: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Update a user's capability setting.
 * Capabilities are granular permissions beyond the basic role.
 */
export async function updateUserCapability(
  userId: string,
  capability: 'canPostCommunityImages',
  enabled: boolean
): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    await updateDoc(profileRef, {
      [capability]: enabled,
    });
  } catch (error) {
    console.error(`Error updating capability ${capability} for user ${userId}:`, error);
    throw error;
  }
}

/**
 * Disable MFA for a user (admin action).
 * This only updates the profile flag. The actual Firebase MFA enrollment
 * can only be removed by the user themselves when they sign in.
 * This is useful for marking MFA as disabled in the profile when
 * Firebase MFA has already been unenrolled.
 */
export async function adminDisableUserMfa(userId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    await updateDoc(profileRef, {
      mfaEnabled: false,
      mfaEnrolledAt: null,
    });
  } catch (error) {
    console.error(`Error disabling MFA for user ${userId}:`, error);
    throw error;
  }
}

/**
 * Delete user data using a batch write for atomicity.
 * All deletions succeed or fail together.
 */
export async function deleteUserData(userId: string): Promise<void> {
  try {
    const batch = writeBatch(db);

    // Delete user profile and stats
    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'info');
    const statsRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'stats');
    batch.delete(profileRef);
    batch.delete(statsRef);

    // Delete attendance logs
    const attendanceRef = collection(db, 'artifacts', appId, 'users', userId, 'attendance_log');
    const attendanceSnap = await getDocs(attendanceRef);
    attendanceSnap.docs.forEach((d) => batch.delete(d.ref));

    await batch.commit();
  } catch (error) {
    console.error(`Error deleting user data for ${userId}:`, error);
    throw error;
  }
}

export async function getUserAttendanceHistory(userId: string): Promise<AttendanceEntry[]> {
  const attendanceRef = collection(db, 'artifacts', appId, 'users', userId, 'attendance_log');
  const q = query(attendanceRef, orderBy('timestamp', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as AttendanceEntry[];
}

export async function searchUsers(searchQuery: string): Promise<AdminUserProfile[]> {
  const users = await getAllUsers();
  const lowerQuery = searchQuery.toLowerCase();

  return users.filter(
    (user) =>
      user.displayName?.toLowerCase().includes(lowerQuery) ||
      user.email?.toLowerCase().includes(lowerQuery) ||
      user.hebrewName?.toLowerCase().includes(lowerQuery)
  );
}

export async function getUsersByStatus(status: UserStatus): Promise<AdminUserProfile[]> {
  const users = await getAllUsers();
  return users.filter((user) => (user.status || 'active') === status);
}

export async function getUsersByRole(role: UserRole): Promise<AdminUserProfile[]> {
  const users = await getAllUsers();
  return users.filter((user) => (user.role || 'member') === role);
}

// ============================================
// ENGAGEMENT ANALYTICS
// ============================================

export async function getEngagementStats(): Promise<EngagementStats> {
  // Get message count
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const messagesSnap = await getDocs(messagesRef);
  const totalMessages = messagesSnap.size;

  // Get prayers count
  const prayersRef = collection(db, 'artifacts', appId, 'public', 'data', 'prayers');
  const prayersSnap = await getDocs(prayersRef);
  const totalPrayers = prayersSnap.size;

  // Get files count
  const filesRef = collection(db, 'artifacts', appId, 'public', 'data', 'files');
  const filesSnap = await getDocs(filesRef);
  const totalFiles = filesSnap.size;

  // Get check-ins and streaks from user stats
  const users = await getAllUsers();
  let totalCheckIns = 0;
  let totalStreak = 0;
  let usersWithStreak = 0;

  for (const user of users) {
    if (user.stats) {
      totalCheckIns += user.stats.totalClasses || 0;
      if (user.stats.streak) {
        totalStreak += user.stats.streak;
        usersWithStreak++;
      }
    }
  }

  return {
    totalCheckIns,
    totalMessages,
    totalPrayers,
    totalFiles,
    averageStreak: usersWithStreak > 0 ? Math.round((totalStreak / usersWithStreak) * 10) / 10 : 0,
  };
}

export async function getPrayersByType(): Promise<Record<PrayerType, number>> {
  const result: Record<PrayerType, number> = {
    healing: 0,
    memory: 0,
    dedication: 0,
  };

  const prayersRef = collection(db, 'artifacts', appId, 'public', 'data', 'prayers');
  const prayersSnap = await getDocs(prayersRef);

  prayersSnap.forEach((doc) => {
    const prayer = doc.data();
    if (prayer.type in result) {
      result[prayer.type as PrayerType]++;
    }
  });

  return result;
}

export async function getTopAttendees(limitCount: number = 10): Promise<AdminUserProfile[]> {
  const users = await getAllUsers();

  return users
    .filter((u) => u.stats && u.stats.totalClasses > 0)
    .sort((a, b) => (b.stats?.totalClasses || 0) - (a.stats?.totalClasses || 0))
    .slice(0, limitCount);
}

// ============================================
// INVITE CODE MANAGEMENT
// ============================================

export async function getAllInviteCodes(): Promise<InviteCode[]> {
  const codesRef = collection(db, 'artifacts', appId, 'invite_codes', 'codes', 'codes');
  const snapshot = await getDocs(codesRef);

  return snapshot.docs.map((doc) => ({
    ...doc.data(),
    code: doc.data().code,
  })) as InviteCode[];
}

export async function getInviteCodeStats(): Promise<InviteCodeStats> {
  const codes = await getAllInviteCodes();

  const activeCodes = codes.filter((c) => c.isActive);
  const totalUses = codes.reduce((sum, c) => sum + (c.usedCount || 0), 0);

  return {
    totalCodes: codes.length,
    activeCodes: activeCodes.length,
    totalUses,
    codesByUsage: codes
      .map((c) => ({
        code: c.code,
        usedCount: c.usedCount || 0,
        maxUses: c.maxUses,
        isActive: c.isActive,
      }))
      .sort((a, b) => b.usedCount - a.usedCount),
  };
}

export async function createInviteCode(options: CreateInviteCodeOptions = {}): Promise<string> {
  const code = options.customCode?.toUpperCase() || generateRandomCode();

  const codeData: Partial<InviteCode> = {
    code,
    createdBy: 'admin',
    createdAt: Timestamp.now(),
    isActive: true,
    usedCount: 0,
    usedBy: [],
  };

  if (options.maxUses) {
    codeData.maxUses = options.maxUses;
  }

  if (options.expiresInDays) {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + options.expiresInDays);
    codeData.expiresAt = Timestamp.fromDate(expiresAt);
  }

  const codesRef = collection(db, 'artifacts', appId, 'invite_codes', 'codes', 'codes');
  await addDoc(codesRef, codeData);

  return code;
}

function generateRandomCode(): string {
  return generateSecureCode(8);
}

export async function revokeInviteCode(code: string): Promise<void> {
  const codesRef = collection(db, 'artifacts', appId, 'invite_codes', 'codes', 'codes');
  const q = query(codesRef, where('code', '==', code.toUpperCase()));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    await updateDoc(snapshot.docs[0].ref, { isActive: false });
  }
}

export async function reactivateInviteCode(code: string): Promise<void> {
  const codesRef = collection(db, 'artifacts', appId, 'invite_codes', 'codes', 'codes');
  const q = query(codesRef, where('code', '==', code.toUpperCase()));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    await updateDoc(snapshot.docs[0].ref, { isActive: true });
  }
}

// ============================================
// CHAT MODERATION
// ============================================

export async function getAllMessages(limitCount?: number): Promise<AdminMessage[]> {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const q = limitCount
    ? query(messagesRef, orderBy('timestamp', 'desc'), limit(limitCount))
    : query(messagesRef, orderBy('timestamp', 'desc'), limit(1000));

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as AdminMessage[];
}

export async function deleteMessage(messageId: string): Promise<void> {
  const messageRef = doc(db, 'artifacts', appId, 'public', 'data', 'messages', messageId);
  await deleteDoc(messageRef);
}

export async function deleteMessagesBatch(messageIds: string[]): Promise<void> {
  const deletePromises = messageIds.map((id) =>
    deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'messages', id))
  );
  await Promise.all(deletePromises);
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

export function subscribeToMessages(
  callback: (messages: AdminMessage[]) => void,
  limitCount: number = 50
): () => void {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(limitCount));

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as AdminMessage[];
    callback(messages);
  });
}

export function subscribeToRecentActivity(callback: (events: ActivityEvent[]) => void): () => void {
  // Subscribe to messages for activity feed
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'), limit(20));

  return onSnapshot(messagesQuery, (snapshot) => {
    const events: ActivityEvent[] = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        type: 'message' as const,
        userId: data.userId,
        userName: data.author || 'Unknown',
        timestamp: data.timestamp,
        metadata: { text: data.text?.substring(0, 50) },
      };
    });
    callback(events);
  });
}

/**
 * Subscribe to messages with reaction data for real-time display.
 * Returns messages with their reaction summaries.
 */
export function subscribeToMessagesWithReactions(
  callback: (messages: AdminMessage[]) => void,
  limitCount: number = 30
): () => void {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(limitCount));

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        text: data.text || '',
        userId: data.userId || '',
        author: data.author || 'Unknown',
        timestamp: data.timestamp,
        reactionSummary: data.reactionSummary || {},
      };
    }) as AdminMessage[];
    callback(messages);
  });
}

/**
 * Get live statistics for the real-time dashboard.
 */
export async function getLiveStats(): Promise<LiveStats> {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Get messages
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const messagesSnap = await getDocs(query(messagesRef, limit(1000)));

  let totalMessages = 0;
  let totalReactions = 0;
  let messagesLast24h = 0;
  let reactionsLast24h = 0;

  messagesSnap.forEach((doc) => {
    const data = doc.data();
    totalMessages++;

    // Check if message is from last 24 hours
    const msgDate = data.timestamp?.toDate();
    if (msgDate && msgDate >= yesterday) {
      messagesLast24h++;
    }

    // Count reactions
    const reactionSummary = data.reactionSummary;
    if (reactionSummary) {
      for (const summary of Object.values(reactionSummary)) {
        const count = (summary as { count: number }).count || 0;
        totalReactions += count;

        // We can't track reaction timestamps without additional data
        // So we'll estimate based on message timestamp
        if (msgDate && msgDate >= yesterday) {
          reactionsLast24h += count;
        }
      }
    }
  });

  // Get active users (users active in last week)
  const users = await getAllUsers();
  let activeUsers = 0;
  for (const user of users) {
    const lastActive = user.lastActiveAt?.toDate() || user.lastLoginAt?.toDate();
    if (lastActive && lastActive >= oneWeekAgo) {
      activeUsers++;
    }
  }

  return {
    totalMessages,
    totalReactions,
    activeUsers,
    messagesLast24h,
    reactionsLast24h,
  };
}

/**
 * Subscribe to announcements for real-time display.
 */
export function subscribeToAnnouncements(
  callback: (
    announcements: Array<{
      id: string;
      title: string;
      content: string;
      authorName: string;
      createdAt: Timestamp;
      priority: string;
      isActive: boolean;
    }>
  ) => void,
  limitCount: number = 5
): () => void {
  const announcementsRef = collection(db, 'artifacts', appId, 'public', 'data', 'announcements');
  const q = query(announcementsRef, orderBy('createdAt', 'desc'), limit(limitCount));

  return onSnapshot(q, (snapshot) => {
    const announcements = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || '',
        content: data.content || '',
        authorName: data.authorName || 'Admin',
        createdAt: data.createdAt,
        priority: data.priority || 'normal',
        isActive: data.isActive !== false,
      };
    });
    callback(announcements);
  });
}

// ============================================
// DATE-BASED ANALYTICS (for charts)
// ============================================

export async function getRegistrationsByDate(days: number = 30): Promise<DateCount[]> {
  const users = await getAllUsers();
  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  const countsByDate: Record<string, number> = {};

  // Initialize all dates
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = date.toISOString().split('T')[0];
    countsByDate[dateStr] = 0;
  }

  // Count registrations
  for (const user of users) {
    if (user.createdAt) {
      const date = user.createdAt.toDate();
      if (date >= startDate) {
        const dateStr = date.toISOString().split('T')[0];
        if (dateStr in countsByDate) {
          countsByDate[dateStr]++;
        }
      }
    }
  }

  return Object.entries(countsByDate)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export async function getMessagesByDate(days: number = 30): Promise<DateCount[]> {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const snapshot = await getDocs(query(messagesRef, limit(1000)));

  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  const countsByDate: Record<string, number> = {};

  // Initialize all dates
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = date.toISOString().split('T')[0];
    countsByDate[dateStr] = 0;
  }

  // Count messages
  snapshot.forEach((doc) => {
    const data = doc.data();
    if (data.timestamp) {
      const date = data.timestamp.toDate();
      if (date >= startDate) {
        const dateStr = date.toISOString().split('T')[0];
        if (dateStr in countsByDate) {
          countsByDate[dateStr]++;
        }
      }
    }
  });

  return Object.entries(countsByDate)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// ============================================
// LEADERBOARD / GAMIFICATION
// ============================================

// NOTE: Types for leaderboard and reactions imported via top-level imports
import type { ReactionSummary } from '@/types/reactions';

export async function getLeaderboard(
  type: LeaderboardType,
  limitCount: number = 10
): Promise<LeaderboardEntry[]> {
  const users = await getAllUsers();

  let sortedUsers = users.filter((u) => u.stats);

  // For reading leaderboard, we need to fetch reading progress for each user
  if (type === 'reading') {
    // Fetch reading progress for all users
    const usersWithReading = await Promise.all(
      sortedUsers.map(async (user) => {
        try {
          const readingProgress = await getReadingProgress(user.uid);
          return {
            ...user,
            readingProgress,
          };
        } catch {
          return {
            ...user,
            readingProgress: null,
          };
        }
      })
    );

    // Sort by total reading time
    const sorted = usersWithReading
      .filter((u) => u.readingProgress && u.readingProgress.totalReadingTimeMs > 0)
      .sort(
        (a, b) =>
          (b.readingProgress?.totalReadingTimeMs || 0) -
          (a.readingProgress?.totalReadingTimeMs || 0)
      );

    return sorted.slice(0, limitCount).map((user, index) => ({
      userId: user.uid,
      displayName: user.displayName,
      hebrewName: user.hebrewName,
      photoURL: user.photoURL,
      totalClasses: user.stats?.totalClasses || 0,
      streak: user.stats?.streak || 0,
      rank: index + 1,
      totalReadingTimeMs: user.readingProgress?.totalReadingTimeMs || 0,
      totalVersesRead: user.readingProgress?.totalVersesRead || 0,
      totalParshasCompleted: user.readingProgress?.totalParshasCompleted || 0,
    }));
  }

  switch (type) {
    case 'classes':
      sortedUsers = sortedUsers.sort(
        (a, b) => (b.stats?.totalClasses || 0) - (a.stats?.totalClasses || 0)
      );
      break;
    case 'streak':
      sortedUsers = sortedUsers.sort((a, b) => (b.stats?.streak || 0) - (a.stats?.streak || 0));
      break;
    case 'thisWeek': {
      // Filter users active this week and sort by recent activity
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      sortedUsers = sortedUsers
        .filter((u) => {
          const lastAttended = u.stats?.lastAttended?.toDate();
          return lastAttended && lastAttended >= oneWeekAgo;
        })
        .sort((a, b) => {
          const aTime = a.stats?.lastAttended?.toMillis() || 0;
          const bTime = b.stats?.lastAttended?.toMillis() || 0;
          return bTime - aTime;
        });
      break;
    }
  }

  return sortedUsers.slice(0, limitCount).map((user, index) => ({
    userId: user.uid,
    displayName: user.displayName,
    hebrewName: user.hebrewName,
    photoURL: user.photoURL,
    totalClasses: user.stats?.totalClasses || 0,
    streak: user.stats?.streak || 0,
    rank: index + 1,
  }));
}

export async function getStreakDistribution(): Promise<StreakDistribution[]> {
  const users = await getAllUsers();

  const distribution: Record<string, number> = {
    '0': 0,
    '1-3': 0,
    '4-7': 0,
    '8-14': 0,
    '15+': 0,
  };

  for (const user of users) {
    const streak = user.stats?.streak || 0;
    if (streak === 0) distribution['0']++;
    else if (streak <= 3) distribution['1-3']++;
    else if (streak <= 7) distribution['4-7']++;
    else if (streak <= 14) distribution['8-14']++;
    else distribution['15+']++;
  }

  return Object.entries(distribution).map(([range, count]) => ({
    range,
    count,
  }));
}

export async function getRetentionMetrics(): Promise<RetentionMetrics> {
  const users = await getAllUsers();
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  let weeklyActive = 0;
  let monthlyActive = 0;

  for (const user of users) {
    const lastActive = user.lastActiveAt?.toDate() || user.lastLoginAt?.toDate();
    if (lastActive) {
      if (lastActive >= oneWeekAgo) weeklyActive++;
      if (lastActive >= oneMonthAgo) monthlyActive++;
    }
  }

  const totalUsers = users.length;
  const retentionRate = totalUsers > 0 ? Math.round((weeklyActive / totalUsers) * 100) : 0;

  return {
    weeklyActive,
    monthlyActive,
    totalUsers,
    retentionRate,
  };
}

export async function getAttendanceByDayOfWeek(): Promise<DayOfWeekStats[]> {
  const users = await getAllUsers();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const counts: Record<string, number> = {};

  for (const day of days) {
    counts[day] = 0;
  }

  // Count last attended by day of week
  for (const user of users) {
    if (user.stats?.lastAttended) {
      const dayOfWeek = user.stats.lastAttended.toDate().getDay();
      counts[days[dayOfWeek]]++;
    }
  }

  return days.map((day) => ({
    day,
    count: counts[day],
  }));
}

export async function getMostActiveThisWeek(limitCount: number = 5): Promise<LeaderboardEntry[]> {
  return getLeaderboard('thisWeek', limitCount);
}

export async function getClassesLeaderboard(limitCount: number = 10): Promise<LeaderboardEntry[]> {
  return getLeaderboard('classes', limitCount);
}

export async function getStreakLeaderboard(limitCount: number = 10): Promise<LeaderboardEntry[]> {
  return getLeaderboard('streak', limitCount);
}

// ============================================
// EMOJI / REACTION ANALYTICS
// ============================================

function processReactionSummary(
  reactionSummary: ReactionSummary,
  emojiCounts: Record<string, number>,
  reactorCounts: Record<string, { count: number; name: string; emojis: Record<string, number> }>
): { messageReactionCount: number; reactions: ReactionDetail[] } {
  let messageReactionCount = 0;
  const reactions: ReactionDetail[] = [];

  for (const [emoji, summary] of Object.entries(reactionSummary)) {
    const count = summary.count || 0;
    emojiCounts[emoji] = (emojiCounts[emoji] || 0) + count;
    messageReactionCount += count;

    if (summary.users && summary.userNames) {
      const maxIndex = Math.min(summary.users.length, summary.userNames.length);
      for (let i = 0; i < maxIndex; i++) {
        const userId = summary.users[i];
        const userName = summary.userNames[i];

        reactions.push({ emoji, userId, userName });

        if (!reactorCounts[userId]) {
          reactorCounts[userId] = { count: 0, name: userName, emojis: {} };
        }
        reactorCounts[userId].count++;
        reactorCounts[userId].emojis[emoji] = (reactorCounts[userId].emojis[emoji] || 0) + 1;
      }
    }
  }

  return { messageReactionCount, reactions };
}

function buildTopEmojis(
  emojiCounts: Record<string, number>,
  totalReactions: number,
  topCount: number
): EmojiCount[] {
  const sortedEmojis = Object.entries(emojiCounts)
    .map(([emoji, count]) => ({ emoji, count }))
    .sort((a, b) => b.count - a.count);

  return sortedEmojis.slice(0, topCount).map((item) => ({
    emoji: item.emoji,
    count: item.count,
    percentage: totalReactions > 0 ? Math.round((item.count / totalReactions) * 100) : 0,
  }));
}

function buildTopReactors(
  reactorCounts: Record<string, { count: number; name: string; emojis: Record<string, number> }>
): TopReactor[] {
  return Object.entries(reactorCounts)
    .map(([userId, data]) => {
      const favoriteEmoji = Object.entries(data.emojis).sort((a, b) => b[1] - a[1])[0]?.[0];
      return {
        userId,
        userName: data.name,
        reactionCount: data.count,
        favoriteEmoji,
      };
    })
    .sort((a, b) => b.reactionCount - a.reactionCount)
    .slice(0, 10);
}

export async function getEmojiStats(topCount: number = 10): Promise<EmojiStats> {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const snapshot = await getDocs(query(messagesRef, limit(1000)));

  const emojiCounts: Record<string, number> = {};
  let totalReactions = 0;
  const mostReactedMessages: ReactedMessage[] = [];
  const reactorCounts: Record<
    string,
    { count: number; name: string; emojis: Record<string, number> }
  > = {};

  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const reactionSummary = data.reactionSummary as ReactionSummary | undefined;

    if (reactionSummary && Object.keys(reactionSummary).length > 0) {
      const { messageReactionCount, reactions } = processReactionSummary(
        reactionSummary,
        emojiCounts,
        reactorCounts
      );
      totalReactions += messageReactionCount;

      if (messageReactionCount > 0) {
        mostReactedMessages.push({
          messageId: docSnap.id,
          messagePreview:
            (data.text || '').substring(0, 80) + (data.text?.length > 80 ? '...' : ''),
          authorId: data.userId || '',
          authorName: data.author || 'Unknown',
          reactionCount: messageReactionCount,
          reactions,
          timestamp: data.timestamp,
        });
      }
    }
  });

  const topEmojis = buildTopEmojis(emojiCounts, totalReactions, topCount);
  mostReactedMessages.sort((a, b) => b.reactionCount - a.reactionCount);
  const topReactors = buildTopReactors(reactorCounts);

  return {
    totalReactions,
    uniqueEmojis: Object.keys(emojiCounts).length,
    topEmojis,
    mostReactedMessages: mostReactedMessages.slice(0, 10),
    topReactors,
  };
}

/**
 * Get detailed reaction data for a specific message.
 */
export async function getMessageReactionDetails(messageId: string): Promise<ReactedMessage | null> {
  const messageSnap = await getDocs(
    query(
      collection(db, 'artifacts', appId, 'public', 'data', 'messages'),
      where('__name__', '==', messageId)
    )
  );

  if (messageSnap.empty) return null;

  const docSnap = messageSnap.docs[0];
  const data = docSnap.data();
  const reactionSummary = data.reactionSummary as ReactionSummary | undefined;

  const reactions: ReactionDetail[] = [];
  let reactionCount = 0;

  if (reactionSummary) {
    for (const [emoji, summary] of Object.entries(reactionSummary)) {
      reactionCount += summary.count || 0;

      if (summary.users && summary.userNames) {
        // Use min of both array lengths to ensure safe access
        const maxIndex = Math.min(summary.users.length, summary.userNames.length);
        for (let i = 0; i < maxIndex; i++) {
          reactions.push({
            emoji,
            userId: summary.users[i],
            userName: summary.userNames[i],
          });
        }
      }
    }
  }

  return {
    messageId: docSnap.id,
    messagePreview: data.text || '',
    authorId: data.userId || '',
    authorName: data.author || 'Unknown',
    reactionCount,
    reactions,
    timestamp: data.timestamp,
  };
}

/**
 * Get emoji usage over time (reactions by date).
 * Aggregates reactions across messages by their timestamp.
 */
export async function getReactionsByDate(days: number = 30): Promise<DateCount[]> {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const snapshot = await getDocs(query(messagesRef, limit(1000)));

  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  const countsByDate: Record<string, number> = {};

  // Initialize all dates
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = date.toISOString().split('T')[0];
    countsByDate[dateStr] = 0;
  }

  // For simplicity, we'll attribute reactions to the message date
  // A more accurate approach would store reaction timestamps separately
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const reactionSummary = data.reactionSummary as ReactionSummary | undefined;

    if (reactionSummary && data.timestamp) {
      const messageDate = data.timestamp.toDate();

      if (messageDate >= startDate) {
        const dateStr = messageDate.toISOString().split('T')[0];
        if (dateStr in countsByDate) {
          // Sum all reactions for this message
          const reactionCount = Object.values(reactionSummary).reduce(
            (sum, s) => sum + (s.count || 0),
            0
          );
          countsByDate[dateStr] += reactionCount;
        }
      }
    }
  });

  return Object.entries(countsByDate)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// ============================================
// APP CONFIGURATION
// ============================================

export interface AppConfig {
  inviteLinkHostname?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

/**
 * Get the app configuration document.
 */
export async function getAppConfig(): Promise<AppConfig> {
  const configRef = doc(db, 'artifacts', appId, 'config', 'app');
  const configSnap = await getDoc(configRef);

  if (configSnap.exists()) {
    return configSnap.data() as AppConfig;
  }

  return {};
}

/**
 * Update the app configuration.
 */
export async function updateAppConfig(
  config: Partial<AppConfig>,
  updatedBy?: string
): Promise<void> {
  const configRef = doc(db, 'artifacts', appId, 'config', 'app');
  await setDoc(
    configRef,
    {
      ...config,
      updatedAt: serverTimestamp(),
      ...(updatedBy && { updatedBy }),
    },
    { merge: true }
  );
}

/**
 * Get the invite link hostname from app config.
 */
export async function getInviteLinkHostname(): Promise<string | null> {
  const config = await getAppConfig();
  return config.inviteLinkHostname || null;
}

/**
 * Set the invite link hostname in app config.
 */
export async function setInviteLinkHostname(
  hostname: string | null,
  updatedBy?: string
): Promise<void> {
  await updateAppConfig({ inviteLinkHostname: hostname || undefined }, updatedBy);
}

// ============================================
// UPDATE HINT BROADCAST
// ============================================

/**
 * Broadcast a refresh hint to all connected clients.
 *
 * When an admin triggers this, all users with the app open will see
 * a toast notification suggesting they refresh to get the latest version.
 */
export async function broadcastRefreshHint(): Promise<void> {
  const updateHintRef = doc(db, 'artifacts', appId, 'config', 'update_hint');
  await setDoc(updateHintRef, {
    updatedAt: serverTimestamp(),
  });
}

/**
 * Force refresh a specific user's browser.
 *
 * The user will see a non-dismissible modal requiring them to refresh.
 * Optionally schedule for a future time.
 * With autoRefresh enabled, the browser will automatically reload.
 */
export async function forceRefreshUser(
  userId: string,
  options?: {
    reason?: string;
    scheduledAt?: Date;
    autoRefresh?: boolean;
    autoRefreshDelay?: number;
  }
): Promise<void> {
  const forceRefreshRef = doc(db, 'artifacts', appId, 'users', userId, 'force_refresh', 'current');
  // Generate unique ID for this force refresh trigger
  const refreshId = randomId('fr');
  await setDoc(forceRefreshRef, {
    id: refreshId,
    triggeredAt: serverTimestamp(),
    effectiveAt: options?.scheduledAt ? Timestamp.fromDate(options.scheduledAt) : serverTimestamp(),
    reason: options?.reason || null,
    acknowledged: false,
    autoRefresh: options?.autoRefresh ?? true,
    autoRefreshDelay: options?.autoRefreshDelay ?? 10,
  });
}

/**
 * Force refresh all users' browsers.
 *
 * All users will see a non-dismissible modal requiring them to refresh.
 * Optionally schedule for a future time.
 * With autoRefresh enabled, the browser will automatically reload.
 */
export async function forceRefreshAllUsers(options?: {
  reason?: string;
  scheduledAt?: Date;
  autoRefresh?: boolean;
  autoRefreshDelay?: number;
}): Promise<void> {
  const forceRefreshRef = doc(db, 'artifacts', appId, 'config', 'force_refresh');
  // Generate unique ID for this force refresh trigger
  const refreshId = randomId('fr');
  await setDoc(forceRefreshRef, {
    id: refreshId,
    triggeredAt: serverTimestamp(),
    effectiveAt: options?.scheduledAt ? Timestamp.fromDate(options.scheduledAt) : serverTimestamp(),
    reason: options?.reason || null,
    autoRefresh: options?.autoRefresh ?? true,
    autoRefreshDelay: options?.autoRefreshDelay ?? 10,
  });
}

/**
 * Cancel a scheduled force refresh for all users.
 */
export async function cancelForceRefreshAll(): Promise<void> {
  const forceRefreshRef = doc(db, 'artifacts', appId, 'config', 'force_refresh');
  await deleteDoc(forceRefreshRef);
}

/**
 * Cancel a scheduled force refresh for a specific user.
 */
export async function cancelForceRefreshUser(userId: string): Promise<void> {
  const forceRefreshRef = doc(db, 'artifacts', appId, 'users', userId, 'force_refresh', 'current');
  await deleteDoc(forceRefreshRef);
}

// ============================================
// DONATION CONFIGURATION
// ============================================

import type { DonationConfig, DonationTrigger } from '@/types/donation';
import { DEFAULT_DONATION_CONFIG } from '@/types/donation'; // eslint-disable-line no-duplicate-imports

/**
 * Get the donation configuration document.
 * Merges with defaults to ensure all properties exist.
 */
export async function getDonationConfig(): Promise<DonationConfig> {
  const configRef = doc(db, 'artifacts', appId, 'config', 'donation');
  const configSnap = await getDoc(configRef);

  if (configSnap.exists()) {
    const data = configSnap.data();
    // Merge with defaults to handle missing properties
    return {
      enabled: data.enabled ?? DEFAULT_DONATION_CONFIG.enabled,
      globalCooldownHours: data.globalCooldownHours ?? DEFAULT_DONATION_CONFIG.globalCooldownHours,
      dismissCooldownHours:
        data.dismissCooldownHours ?? DEFAULT_DONATION_CONFIG.dismissCooldownHours,
      maxPromptsPerWeek: data.maxPromptsPerWeek ?? DEFAULT_DONATION_CONFIG.maxPromptsPerWeek,
      triggers: data.triggers ?? DEFAULT_DONATION_CONFIG.triggers,
      display: {
        position: data.display?.position ?? DEFAULT_DONATION_CONFIG.display.position,
        animationStyle:
          data.display?.animationStyle ?? DEFAULT_DONATION_CONFIG.display.animationStyle,
        autoDismissSeconds:
          data.display?.autoDismissSeconds ?? DEFAULT_DONATION_CONFIG.display.autoDismissSeconds,
      },
      updatedAt: data.updatedAt,
      updatedBy: data.updatedBy,
    };
  }

  return DEFAULT_DONATION_CONFIG as DonationConfig;
}

/**
 * Update the donation configuration.
 */
export async function updateDonationConfig(
  config: Partial<DonationConfig>,
  updatedBy?: string
): Promise<void> {
  const configRef = doc(db, 'artifacts', appId, 'config', 'donation');
  await setDoc(
    configRef,
    {
      ...config,
      updatedAt: serverTimestamp(),
      ...(updatedBy && { updatedBy }),
    },
    { merge: true }
  );
}

/**
 * Save a complete donation configuration (replace all).
 */
export async function saveDonationConfig(
  config: DonationConfig,
  updatedBy?: string
): Promise<void> {
  const configRef = doc(db, 'artifacts', appId, 'config', 'donation');
  await setDoc(configRef, {
    ...config,
    updatedAt: serverTimestamp(),
    ...(updatedBy && { updatedBy }),
  });
}

/**
 * Add a new donation trigger.
 */
export async function addDonationTrigger(
  trigger: DonationTrigger,
  updatedBy?: string
): Promise<void> {
  const config = await getDonationConfig();
  const updatedTriggers = [...config.triggers, trigger];

  await updateDonationConfig({ triggers: updatedTriggers }, updatedBy);
}

/**
 * Update an existing donation trigger.
 */
export async function updateDonationTrigger(
  triggerId: string,
  updates: Partial<DonationTrigger>,
  updatedBy?: string
): Promise<void> {
  const config = await getDonationConfig();
  const updatedTriggers = config.triggers.map((t) =>
    t.id === triggerId ? { ...t, ...updates } : t
  );

  await updateDonationConfig({ triggers: updatedTriggers }, updatedBy);
}

/**
 * Delete a donation trigger.
 */
export async function deleteDonationTrigger(triggerId: string, updatedBy?: string): Promise<void> {
  const config = await getDonationConfig();
  const updatedTriggers = config.triggers.filter((t) => t.id !== triggerId);

  await updateDonationConfig({ triggers: updatedTriggers }, updatedBy);
}

/**
 * Toggle donation system enabled/disabled.
 */
export async function toggleDonationSystem(enabled: boolean, updatedBy?: string): Promise<void> {
  await updateDonationConfig({ enabled }, updatedBy);
}

// ============================================
// SESSION & MFA MANAGEMENT (Cloud Functions)
// ============================================

/**
 * Revoke all sessions for a user via Cloud Function.
 * Forces the user to re-authenticate on all devices.
 */
export async function revokeUserSessions(
  targetUserId: string
): Promise<{ success: boolean; message: string }> {
  const { httpsCallable, functions } = await getFirebaseFunctions();
  const callable = httpsCallable<
    { targetUserId: string; appId: string },
    { success: boolean; message: string }
  >(functions, 'revokeUserSessions');

  const result = await callable({ targetUserId, appId });
  return result.data;
}

/**
 * Reset MFA for a user via Cloud Function.
 * Removes all MFA enrollments and revokes sessions.
 */
export async function resetUserMfa(
  targetUserId: string
): Promise<{ success: boolean; message: string; factorsRemoved: number }> {
  const { httpsCallable, functions } = await getFirebaseFunctions();
  const callable = httpsCallable<
    { targetUserId: string; appId: string },
    { success: boolean; message: string; factorsRemoved: number }
  >(functions, 'resetUserMfa');

  const result = await callable({ targetUserId, appId });
  return result.data;
}

/**
 * Force MFA enrollment for a user via Cloud Function.
 */
export async function forceMfaEnrollment(
  targetUserId: string,
  enforcement: MfaEnforcementType,
  graceDays?: number
): Promise<{ success: boolean; message: string; enforcement: string }> {
  const { httpsCallable, functions } = await getFirebaseFunctions();
  const callable = httpsCallable<
    { targetUserId: string; appId: string; enforcement: string; graceDays?: number },
    { success: boolean; message: string; enforcement: string }
  >(functions, 'forceMfaEnrollment');

  const result = await callable({ targetUserId, appId, enforcement, graceDays });
  return result.data;
}

/**
 * Remove forced MFA requirement for a user via Cloud Function.
 */
export async function removeForcedMfa(
  targetUserId: string
): Promise<{ success: boolean; message: string }> {
  const { httpsCallable, functions } = await getFirebaseFunctions();
  const callable = httpsCallable<
    { targetUserId: string; appId: string },
    { success: boolean; message: string }
  >(functions, 'removeForcedMfa');

  const result = await callable({ targetUserId, appId });
  return result.data;
}

// ============================================
// ADMIN AUDIT LOG
// ============================================

/**
 * Get admin audit log entries.
 * Optionally filter by target user or action type.
 */
export async function getAdminAuditLog(options?: {
  targetUserId?: string;
  action?: string;
  limitCount?: number;
}): Promise<AdminAuditEntry[]> {
  const auditLogRef = collection(db, 'artifacts', appId, 'admin_audit_log');
  let q = query(auditLogRef, orderBy('timestamp', 'desc'));

  if (options?.targetUserId) {
    q = query(q, where('targetUserId', '==', options.targetUserId));
  }

  if (options?.action) {
    q = query(q, where('action', '==', options.action));
  }

  if (options?.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as AdminAuditEntry[];
}

/**
 * Subscribe to admin audit log entries in real-time.
 */
export function subscribeToAdminAuditLog(
  callback: (entries: AdminAuditEntry[]) => void,
  options?: {
    targetUserId?: string;
    limitCount?: number;
  }
): () => void {
  const auditLogRef = collection(db, 'artifacts', appId, 'admin_audit_log');
  let q = query(auditLogRef, orderBy('timestamp', 'desc'));

  if (options?.targetUserId) {
    q = query(q, where('targetUserId', '==', options.targetUserId));
  }

  if (options?.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  return onSnapshot(q, (snapshot) => {
    const entries = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as AdminAuditEntry[];
    callback(entries);
  });
}

// ============================================
// USER SESSIONS (Comprehensive Session Tracking)
// ============================================

// Re-export session types and functions from the sessions service
export {
  getUserSessions as getAdminUserSessions,
  deleteSession as deleteAdminUserSession,
  deleteAllSessions as deleteAllAdminUserSessions,
  subscribeToUserSessions as subscribeToAdminUserSessions,
  type SessionData as AdminSessionData,
} from '@/services/sessions';

// Import for admin session functions
import {
  getUserSessions as getSessionsFromService,
  deleteSession as deleteSessionFromService,
  deleteAllSessions as deleteAllSessionsFromService,
  type SessionData,
} from '@/services/sessions';

/**
 * Get active sessions for a user (admin use).
 */
export async function getUserSessions(userId: string) {
  return getSessionsFromService(userId);
}

/**
 * Delete a specific session for a user (admin use).
 */
export async function deleteUserSession(userId: string, sessionId: string): Promise<void> {
  await deleteSessionFromService(userId, sessionId);
}

/**
 * Delete all sessions for a user (admin use).
 */
export async function deleteAllUserSessions(userId: string): Promise<number> {
  return deleteAllSessionsFromService(userId);
}

// ============================================
// USER-SPECIFIC REAL-TIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to a user's active sessions in real-time.
 */
export function subscribeToUserSessions(
  userId: string,
  callback: (sessions: SessionData[]) => void
): () => void {
  const sessionsRef = collection(db, 'artifacts', appId, 'users', userId, 'sessions');
  const activeQuery = query(sessionsRef, orderBy('lastActiveAt', 'desc'));

  return onSnapshot(
    activeQuery,
    (snapshot) => {
      const sessions: SessionData[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as SessionData[];
      callback(sessions);
    },
    (error) => {
      console.error('Error in user sessions subscription:', error);
      callback([]);
    }
  );
}

/**
 * Message type for real-time subscription.
 */
export interface UserMessage {
  id: string;
  text: string;
  timestamp: Timestamp | null;
  author?: string;
  authorRole?: UserRole;
}

/**
 * Subscribe to a user's messages in real-time.
 * Uses client-side filtering to avoid composite index requirement.
 */
export function subscribeToUserMessages(
  userId: string,
  callback: (messages: UserMessage[]) => void,
  limitCount: number = 50
): () => void {
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  // Fetch recent messages and filter client-side to avoid composite index requirement
  const recentQuery = query(messagesRef, orderBy('timestamp', 'desc'), limit(200));

  return onSnapshot(
    recentQuery,
    (snapshot) => {
      const messages: UserMessage[] = snapshot.docs
        .filter((doc) => doc.data().userId === userId)
        .slice(0, limitCount)
        .map((doc) => ({
          id: doc.id,
          text: doc.data().text || '',
          timestamp: doc.data().timestamp || null,
          author: doc.data().author,
          authorRole: doc.data().authorRole,
        }));
      callback(messages);
    },
    (error) => {
      console.error('Error in user messages subscription:', error);
      callback([]);
    }
  );
}

/**
 * Reaction type for real-time subscription.
 */
export interface UserReaction {
  id: string;
  messageId: string;
  emoji: string;
  timestamp: Timestamp | null;
  messagePreview?: string;
}

/**
 * Subscribe to a user's reactions in real-time.
 * NOTE: collectionGroup queries on 'reactions' require special Firestore rules.
 * This implementation queries messages and extracts reactions from them.
 * Falls back to empty array if permissions are insufficient.
 */
export function subscribeToUserReactions(
  userId: string,
  callback: (reactions: UserReaction[]) => void,
  limitCount: number = 50
): () => void {
  // Query messages to find ones with reactions by this user
  // We'll check the reactions subcollection for each message
  const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
  const recentMessages = query(messagesRef, orderBy('timestamp', 'desc'), limit(100));

  return onSnapshot(
    recentMessages,
    async (snapshot) => {
      const reactions: UserReaction[] = [];

      // For each message, check if this user has reactions
      for (const msgDoc of snapshot.docs) {
        try {
          const reactionsRef = collection(msgDoc.ref, 'reactions');
          const userReactionsQuery = query(reactionsRef, where('userId', '==', userId));
          const reactionsSnap = await getDocs(userReactionsQuery);

          reactionsSnap.docs.forEach((reactionDoc) => {
            reactions.push({
              id: reactionDoc.id,
              messageId: msgDoc.id,
              emoji: reactionDoc.data().emoji || '',
              timestamp: reactionDoc.data().createdAt || null, // Reactions use createdAt, not timestamp
            });
          });
        } catch {
          // Skip messages where we can't access reactions
        }
      }

      // Sort by timestamp and limit
      reactions.sort((a, b) => {
        const aTime = a.timestamp?.toMillis?.() || 0;
        const bTime = b.timestamp?.toMillis?.() || 0;
        return bTime - aTime;
      });

      callback(reactions.slice(0, limitCount));
    },
    (error) => {
      console.error('Error in user reactions subscription:', error);
      callback([]);
    }
  );
}

/**
 * Check-in type for real-time subscription.
 */
export interface UserCheckIn {
  id: string;
  portion: string;
  timestamp: Timestamp | null;
}

/**
 * Subscribe to a user's check-ins in real-time.
 */
export function subscribeToUserCheckIns(
  userId: string,
  callback: (checkIns: UserCheckIn[]) => void,
  limitCount: number = 50
): () => void {
  const checkInsRef = collection(db, 'artifacts', appId, 'users', userId, 'attendance_log');
  const checkInsQuery = query(checkInsRef, orderBy('timestamp', 'desc'), limit(limitCount));

  return onSnapshot(
    checkInsQuery,
    (snapshot) => {
      const checkIns: UserCheckIn[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        portion: doc.data().portion || '',
        timestamp: doc.data().timestamp || null,
      }));
      callback(checkIns);
    },
    (error) => {
      console.error('Error in user check-ins subscription:', error);
      callback([]);
    }
  );
}
